/* eslint-disable no-restricted-syntax */

/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { DescriptionPage_ProductVariant as ProductVariant } from '__generated__/graphql-types';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Button, Link, Typography2, typography2 } from '@coursera/cds-core';

import { TrackedA } from 'bundles/page/components/TrackedLink2';
import getProductTitle from 'bundles/unified-common/utils/getProductTitle';
import MultipleSpecializationsModal from 'bundles/unified-description-page-common/components/shared/MultipleSpecializationsModal';
import { useModal } from 'bundles/unified-description-page-common/components/shared/modals/Modal';
import { usePageInfo } from 'bundles/unified-description-page-common/contexts/PageInfoContext';
import type { SpecializationFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/SharedCourseEntitiesFragments';

import _t from 'i18n!nls/xdp';

type Props = {
  specializations?: Array<SpecializationFragment> | null;
};

const styles = {
  container: () => css`
    margin-top: var(--cds-spacing-200);
  `,
  learnMoreButton: css`
    padding: 0;
    margin-left: 2px;

    /* set vertical-align to unset to vertically center the button within line of text. */
    vertical-align: unset;
    ${typography2.bodyPrimary};
    font-weight: 700;

    :hover {
      background: transparent;
    }
  `,
  specializationLink: css`
    font-weight: 600;
    text-decoration: none;

    :hover {
      text-decoration: underline;
    }
  `,
};

const HeroCourseDescription = ({ specializations }: Props) => {
  const { isModalOpen, setModalOpen } = useModal();
  const { getSpecializationLink } = usePageInfo();
  if (!specializations || specializations.length === 0) {
    return null;
  }

  if (specializations?.length > 1) {
    return (
      <div css={styles.container}>
        <Typography2 variant="bodyPrimary" component="div">
          <FormattedMessage
            message={_t('This course is available as part of {multiplePrograms}')}
            multiplePrograms={
              <Button
                variant="ghost"
                size="small"
                edgeAlign="start"
                css={styles.learnMoreButton}
                onClick={setModalOpen}
              >
                {_t('multiple programs')}
              </Button>
            }
          />
          <div>{_t("When you enroll in this course, you'll also be asked to select a specific program.")}</div>
        </Typography2>
        <MultipleSpecializationsModal
          open={isModalOpen}
          setOpen={setModalOpen}
          specializations={specializations}
          keepMounted={false}
          renderInPlace={false}
        />
      </div>
    );
  }

  const specialization = specializations[0];

  return (
    <div css={styles.container}>
      <Typography2 variant="bodyPrimary" component="div">
        <FormattedMessage
          message={_t('This course is part of the {specializationLink}')}
          specializationLink={
            <Link
              component={TrackedA}
              href={getSpecializationLink({
                productSlug: specialization.slug,
                productVariant: specialization.productVariant,
              })}
              trackingName="career_outcomes_program_link"
              css={styles.specializationLink}
            >
              {getProductTitle(specialization.name, specialization.productVariant)}
            </Link>
          }
        />
        <div>
          {specialization.productVariant === ProductVariant.ProfessionalCertificate
            ? _t("When you enroll in this course, you'll also be enrolled in this Professional Certificate.")
            : _t("When you enroll in this course, you'll also be enrolled in this Specialization.")}
        </div>
      </Typography2>
    </div>
  );
};

export default HeroCourseDescription;
